import {Tr} from "@/i18n/i18n";
import {ClanCandidateState, ClanControlAction, ClanPosition, ParticipantInfo} from "@/store/clan/types";
import {Keys} from "@/constants/key";
import {Player, WorldType} from "@/store/player/types";
import {Settings} from "@/constants/settings";
import TimeUtils from "@/utils/time-utils";
import Scene = Phaser.Scene;
import {GameState} from "@/constants/game-state";

export default class ClanUtils {

    public static loadClanTheme(scene: Scene, canvas: HTMLCanvasElement, themeNumber: number) {
        let prefix = 'assets/clan/back_' + String(themeNumber) + "/";

        GameState.CLAN_BACK_KEY = Keys.CLAN_BACK + String(themeNumber);
        let h = canvas.height;
        if (h <= 1000) {
            scene.load.image(GameState.CLAN_BACK_KEY, prefix + '1000.jpg');
        } else if (h <= 1100) {
            scene.load.image(GameState.CLAN_BACK_KEY, prefix + '1100.jpg');
        } else if (h <= 1200) {
            scene.load.image(GameState.CLAN_BACK_KEY, prefix + '1200.jpg');
        } else {
            scene.load.image(GameState.CLAN_BACK_KEY, prefix + '1600.jpg');
        }
    }

    public static getActionName(ca: ClanControlAction): string {
        switch (ca) {
            case ClanControlAction.INVITE:
                return Tr('priglasit.v.klan');
            case ClanControlAction.KICK:
                return Tr('iskljuchit.iz.klana');
            case ClanControlAction.DOWN_TO_NEWBIE:
                return Tr('ponizit.do.novichka');
            case ClanControlAction.DOWN_TO_PARTICIPANT:
                return Tr('ponizit.do.uchastnika');
            case ClanControlAction.DOWN_TO_REPRESENTATIVE:
                return Tr('ponizit.do.predstavitelja');
            case ClanControlAction.UP_TO_PARTICIPANT:
                return Tr('povisit.do.uchastnika');
            case ClanControlAction.UP_TO_REPRESENTATIVE:
                return Tr('povisit.do.predstavitelja');
            case ClanControlAction.UP_TO_DEPUTY:
                return Tr('povisit.do.zam.lidera');
        }
        return '';
    }

    public static getProfileActionImg(ca: ClanControlAction): string {
        switch (ca) {
            case ClanControlAction.INVITE:
                return Keys.CL_INVITE;
            case ClanControlAction.KICK:
                return Keys.CL_KICK;
            case ClanControlAction.DOWN_TO_NEWBIE:
                return Keys.CL_DOWN;
            case ClanControlAction.DOWN_TO_PARTICIPANT:
                return Keys.CL_DOWN;
            case ClanControlAction.DOWN_TO_REPRESENTATIVE:
                return Keys.CL_DOWN;
            case ClanControlAction.UP_TO_PARTICIPANT:
                return Keys.CL_UP;
            case ClanControlAction.UP_TO_REPRESENTATIVE:
                return Keys.CL_UP;
            case ClanControlAction.UP_TO_DEPUTY:
                return Keys.CL_UP;
        }
        return '';
    }

    public static getActionImg(ca: ClanControlAction): string {
        switch (ca) {
            case ClanControlAction.INVITE:
                return Keys.UI_PROFILE_HELP;
            case ClanControlAction.KICK:
                return Keys.UI_DECLINE_CROSS;
            case ClanControlAction.DOWN_TO_NEWBIE:
                return Keys.UI_ART_DECREASE_ICON;
            case ClanControlAction.DOWN_TO_PARTICIPANT:
                return Keys.UI_ART_DECREASE_ICON;
            case ClanControlAction.DOWN_TO_REPRESENTATIVE:
                return Keys.UI_ART_DECREASE_ICON;
            case ClanControlAction.UP_TO_PARTICIPANT:
                return Keys.UI_ART_UPPER_ICON_GREEN;
            case ClanControlAction.UP_TO_REPRESENTATIVE:
                return Keys.UI_ART_UPPER_ICON_GREEN;
            case ClanControlAction.UP_TO_DEPUTY:
                return Keys.UI_ART_UPPER_ICON_GREEN;
        }
        return '';
    }

    public static getActionFeedback(ca: ClanControlAction): string {
        switch (ca) {
            case ClanControlAction.INVITE:
                return Tr('igrok.prigleshen.v.klan');
            case ClanControlAction.KICK:
                return Tr('igrok.izgnan.iz.klana');
            case ClanControlAction.DOWN_TO_NEWBIE:
                return Tr('igrok.ponizhen.do.novichka');
            case ClanControlAction.DOWN_TO_PARTICIPANT:
                return Tr('igrok.ponizhen.do.uchastnika');
            case ClanControlAction.DOWN_TO_REPRESENTATIVE:
                return Tr('igrok.ponizhen.do.predstavitelja');
            case ClanControlAction.UP_TO_PARTICIPANT:
                return Tr('igrok.povishen.do.uchastnika');
            case ClanControlAction.UP_TO_REPRESENTATIVE:
                return Tr('igrok.povishen.do.predstavitelja');
            case ClanControlAction.UP_TO_DEPUTY:
                return Tr('igrok.povishen.do.zam.lidera');
        }
        return '';
    }

    public static getClanPositionName(p: ParticipantInfo | undefined): string {
        if (!p) {
            return Tr('iskljuchen.iz.klana');
        }
        return this.getPositionName(p.clanPosition);
    }

    public static getPositionName(clanPosition: ClanPosition): string {
        switch (clanPosition) {
            case ClanPosition.LEADER:
                return Tr('lider');
            case ClanPosition.DEPUTY_LEADER:
                return Tr('zamestitel.lidera');
            case ClanPosition.REPRESENTATIVE:
                return Tr('predstavitel');
            case ClanPosition.PARTICIPANT:
                return Tr('uchastnik');
            case ClanPosition.NEWBIE:
                return Tr('novichok');
            case ClanPosition.SUBLEADER:
                return Tr('sublead');
        }
        return Tr('chlen');
    }

    public static getStatus(state: ClanCandidateState): string {
        switch (state) {
            case ClanCandidateState.WANTS_TO_ANY_CLAN:
                return Tr('hochet.v.ljuboy.klan');
            case ClanCandidateState.WANTS_TO_THE_CLAN:
                return Tr('hochet.imenno.v.vash.klan');
            case ClanCandidateState.INVITED:
                return Tr('invtd');
            case ClanCandidateState.REJECTED:
                return Tr('rjctd');
        }
    }

    public static promptClan(player: Player): boolean {
        if (player.level < Settings.CLAN_MIN_LEVEL) {
            return false;
        }

        if (player.promptClan) {
            return true;
        }

        if (!player.clanId) {
            return false;
        }

        let d: Date = new Date(TimeUtils.getServerTimeMs());
        if (d.getHours() < 8) { // its a hack
            return false;
        }

        return !!player.raidOpensAt && player.worldType === WorldType.DEFAULT && player.raidOpensAt <= TimeUtils.getServerTimeMs();
    }
}
